import i18n from "@/i18n";
import { tFi, fiDate, fiStatus } from "@/lib/printFi";
import { apiErrorMessage } from "@/lib/apiErrorMessage";
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useRestaurantScope } from "@/hooks/useRestaurantScope";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Printer, FileText } from "lucide-react";
import { toast } from "sonner";

/**
 * Unified print dialog for the Weekly Menu module.
 *
 * mode:
 *   "day_menu"   → one day's menu (single-select day picker)
 *   "week_menu"  → full weekly menu (all 7 days)
 *   "prep"       → daily prep lists (multi-select day picker: 1..7 days)
 *
 * The prep flow lets the chef print any combination of days in one
 * printout. Presets are provided for "Selected day", "Today", "Today
 * + Tomorrow" and "Whole week".
 */
export default function PrintMenuDialog({ mode, menu, days, selectedDay, onClose }) {
    const { user, restaurantId } = useApp();
    const { scopedParams: params } = useRestaurantScope();

    const isPrep = mode === "prep";
    const needsDayPick = mode === "day_menu";

    const [pickedDay, setPickedDay] = useState(selectedDay || null);
    const [prepDates, setPrepDates] = useState(() =>
        selectedDay ? [selectedDay.date] : []);
    const [prepBundle, setPrepBundle] = useState(null);
    const [busy, setBusy] = useState(false);

    const toggleDate = (date) => setPrepDates((prev) =>
        prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]);
    const selectAll = () => setPrepDates(days.map((d) => d.date));
    const clearAll = () => setPrepDates([]);
    const selectToday = () => {
        const today = new Date().toISOString().slice(0, 10);
        const has = days.find((d) => d.date === today);
        setPrepDates(has ? [today] : days[0] ? [days[0].date] : []);
    };
    const selectTodayTomorrow = () => {
        const today = new Date().toISOString().slice(0, 10);
        const idx = days.findIndex((d) => d.date === today);
        if (idx === -1) setPrepDates(days.slice(0, 2).map((d) => d.date));
        else setPrepDates(days.slice(idx, idx + 2).map((d) => d.date));
    };

    // Load the prep bundle whenever the picked set changes.
    const prepDatesKey = prepDates.join(",");
    useEffect(() => {
        if (!isPrep) return;
        const dates = prepDatesKey ? prepDatesKey.split(",") : [];
        if (dates.length === 0) { setPrepBundle(null); return; }
        setBusy(true);
        (async () => {
            try {
                const sorted = [...dates].sort();
                const start_date = sorted[0];
                const range_type = sorted.length === 1 ? "day" : "week";
                const { data } = await api.get(
                    "/weekly-menu/daily-prep/print",
                    { params: { ...params, start_date, range_type } });
                // Filter the returned bundle down to the days the user picked.
                const picked = new Set(sorted);
                const filtered = (data.days || []).filter((d) => picked.has(d.date));
                setPrepBundle({ ...data, days: filtered });
            } catch (e) {
                toast.error(apiErrorMessage(e));
            } finally { setBusy(false); }
        })();
    }, [isPrep, prepDatesKey, params]);

    const canPrint = isPrep
        ? !!prepBundle && !busy && prepDates.length > 0
        : (mode === "week_menu" ? !!menu : !!pickedDay);
    const doPrint = () => setTimeout(() => window.print(), 100);

    const title = {
        day_menu:  i18n.t("ui.printMenuDialog.printDayMenu", "Print — Day Menu"),
        week_menu: i18n.t("ui.printMenuDialog.printWeeklyMenu", "Print — Weekly Menu"),
        prep:      i18n.t("ui.printMenuDialog.printDailyPrepLists", "Print — Daily Prep Lists"),
    }[mode];

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto"
                             data-testid="print-menu-dialog">
                <DialogHeader className="print:hidden">
                    <DialogTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" /> {title}
                    </DialogTitle>
                    <DialogDescription>
                        Preview below. Use your browser&apos;s print dialog for A4 output.
                    </DialogDescription>
                </DialogHeader>

                {needsDayPick && !pickedDay && (
                    <div className="py-6 print:hidden" data-testid="print-day-picker">
                        <div className="text-sm mb-3">{i18n.t("ui.printMenuDialog.whichDayWouldYouLikeTo", "Which day would you like to print?")}</div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {days.map((d) => (
                                <button
                                    key={d.date}
                                    data-testid={`print-pick-${d.day_name.toLowerCase()}`}
                                    onClick={() => setPickedDay(d)}
                                    className="h-14 rounded-md border border-neutral-300 text-sm font-semibold hover:bg-neutral-50"
                                >
                                    <div>{d.day_name}</div>
                                    <div className="text-xs text-neutral-500">{d.date}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {isPrep && (
                    <div className="py-4 print:hidden" data-testid="print-prep-multi-picker">
                        <div className="text-sm font-semibold mb-2">{i18n.t("ui.printMenuDialog.selectTheDaysToInclude", "Select the days to include")}</div>
                        <div className="flex flex-wrap gap-2 mb-3 text-xs">
                            <button data-testid="prep-preset-today"
                                    onClick={selectToday}
                                    className="h-8 px-3 rounded-md border border-neutral-300 hover:bg-neutral-50">
                                {i18n.t("ui.printMenuDialog.today", "Today")}
                            </button>
                            <button data-testid="prep-preset-today-tomorrow"
                                    onClick={selectTodayTomorrow}
                                    className="h-8 px-3 rounded-md border border-neutral-300 hover:bg-neutral-50">
                                {i18n.t("ui.printMenuDialog.todayTomorrow", "Today + Tomorrow")}
                            </button>
                            <button data-testid="prep-preset-all"
                                    onClick={selectAll}
                                    className="h-8 px-3 rounded-md border border-neutral-300 hover:bg-neutral-50">
                                {i18n.t("ui.printMenuDialog.wholeWeek", "Whole week")}
                            </button>
                            <button data-testid="prep-preset-clear"
                                    onClick={clearAll}
                                    className="h-8 px-3 rounded-md border border-neutral-300 hover:bg-neutral-50">
                                {i18n.t("ui.printMenuDialog.clear", "Clear")}
                            </button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {days.map((d) => {
                                const checked = prepDates.includes(d.date);
                                return (
                                    <label
                                        key={d.date}
                                        data-testid={`prep-day-${d.day_name.toLowerCase()}`}
                                        className={`cursor-pointer h-16 rounded-md border text-sm font-semibold px-3 py-2 flex flex-col justify-center transition ${
                                            checked
                                                ? "border-neutral-900 bg-neutral-900 text-white"
                                                : "border-neutral-300 hover:bg-neutral-50"
                                        }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                data-testid={`prep-day-cb-${d.day_name.toLowerCase()}`}
                                                checked={checked}
                                                onChange={() => toggleDate(d.date)}
                                                className="h-4 w-4 accent-white"
                                            />
                                            <span>{d.day_name}</span>
                                        </div>
                                        <div className={`text-xs mt-0.5 ${checked ? "text-neutral-300" : "text-neutral-500"}`}>
                                            {d.date}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                        <div className="mt-2 text-xs text-neutral-500"
                             data-testid="prep-selected-count">
                            {prepDates.length === 0
                                ? i18n.t("ui.printMenuDialog.noDaysSelected", "No days selected.")
                                : `${prepDates.length} day${prepDates.length > 1 ? "s" : ""} selected.`}
                        </div>
                    </div>
                )}

                <div className="flex justify-end print:hidden py-2">
                    <button
                        data-testid="print-do"
                        onClick={doPrint}
                        disabled={!canPrint}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                    ><Printer className="h-4 w-4" /> {i18n.t("ui.printMenuDialog.print", "Print")}</button>
                </div>

                {busy && <div className="py-6 text-center text-sm text-neutral-500">{i18n.t("common.loading", "Loading…")}</div>}

                {mode === "day_menu" && pickedDay && (
                    <DayMenuPage menu={menu} day={pickedDay} />
                )}
                {mode === "week_menu" && menu && (
                    <WeekMenuPage menu={menu} days={days} />
                )}
                {mode === "prep" && prepBundle && prepBundle.days.length > 0 && (
                    <PrepPages days={prepBundle.days} />
                )}
            </DialogContent>
        </Dialog>
    );
}

// ─────────────────────────────────────────────── Shared page shell
function PageHeader({ eyebrow, title, right }) {
    return (
        <header className="mb-6 border-b-2 border-neutral-900 pb-3 flex items-baseline justify-between">
            <div>
                <div className="text-xs uppercase tracking-[0.32em] text-neutral-600">{eyebrow}</div>
                <div className="font-display text-3xl font-extrabold">{title}</div>
            </div>
            {right}
        </header>
    );
}

function DishRow({ dish }) {
    return (
        <div className="mb-3 break-inside-avoid">
            <div className="font-semibold text-base">{dish.dish_name}</div>
            {dish.dish_description && (
                <div className="text-sm text-neutral-600">{dish.dish_description}</div>
            )}
            {(dish.planned_pax || dish.pax) && (
                <div className="text-xs text-neutral-500 mt-0.5">
                    {tFi("weeklyMenu.plannedPax")}: <strong>{dish.planned_pax || dish.pax}</strong>
                </div>
            )}
        </div>
    );
}

function DayHeading({ day }) {
    const parsed = day.date && new Date(day.date);
    const dateLabel = parsed
        ? fiDate(day.date, { weekday: "long", year: "numeric", month: "long", day: "numeric" })
        : day.day_name;
    return (
        <div className="border-b border-neutral-300 mb-3 pb-1 flex items-baseline justify-between">
            <h2 className="font-display text-2xl font-bold">{dateLabel}</h2>
            {day.status && (
                <span className="text-xs uppercase tracking-wide text-neutral-500">
                    {fiStatus(day.status)}
                </span>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────── Weekly Menu — day
function DayMenuPage({ menu, day }) {
    return (
        <div className="p-8 bg-white text-neutral-900 print:p-0" data-testid="print-day-menu">
            <PageHeader
                eyebrow="MiseOne"
                title={tFi("ui.printMenuDialog.menu", "Menu")}
                right={menu?.restaurant_name && (
                    <div className="text-sm text-neutral-500">{menu.restaurant_name}</div>
                )}
            />
            <DayHeading day={day} />
            {(!day.dishes || day.dishes.length === 0) ? (
                <p className="text-sm text-neutral-500 italic">{tFi("ui.printMenuDialog.noDishesPlannedForThisDay", "No dishes planned for this day.")}</p>
            ) : (
                day.dishes.map((d, i) => <DishRow key={i} dish={d} />)
            )}
            {day.notes && (
                <div className="mt-6 text-sm">
                    <div className="text-xs uppercase tracking-wide text-neutral-500 mb-1">{tFi("common.notes", "Notes")}</div>
                    <div>{day.notes}</div>
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────── Weekly Menu — week
function WeekMenuPage({ menu, days }) {
    const first = days[0]?.date, last = days[days.length - 1]?.date;
    return (
        <div className="p-8 bg-white text-neutral-900 print:p-0" data-testid="print-week-menu">
            <PageHeader
                eyebrow="MiseOne"
                title={tFi("ui.printMenuDialog.weeklyMenu", "Weekly Menu")}
                right={
                    <div className="text-sm text-neutral-500 text-right">
                        {menu?.restaurant_name && <div>{menu.restaurant_name}</div>}
                        {first && last && <div>{fiDate(first)} – {fiDate(last)}</div>}
                    </div>
                }
            />
            {days.map((day) => (
                <section key={day.date}
                         className="mb-8 break-inside-avoid page-break-after-avoid">
                    <DayHeading day={day} />
                    {(!day.dishes || day.dishes.length === 0) ? (
                        <p className="text-sm text-neutral-500 italic">{tFi("ui.printMenuDialog.noDishesPlanned", "No dishes planned.")}</p>
                    ) : (
                        day.dishes.map((d, i) => <DishRow key={i} dish={d} />)
                    )}
                    {day.notes && (
                        <div className="mt-2 text-sm">
                            <div className="text-xs uppercase tracking-wide text-neutral-500 mb-1">{tFi("common.notes", "Notes")}</div>
                            <div>{day.notes}</div>
                        </div>
                    )}
                </section>
            ))}
        </div>
    );
}

// ─────────────────────────────────────────────── Prep — 1..N days
function PrepPages({ days }) {
    const isMulti = days.length > 1;
    const testId = isMulti ? "print-prep-multi" : "print-day-prep";
    return (
        <div className="p-8 bg-white text-neutral-900 print:p-0" data-testid={testId}>
            <PageHeader
                eyebrow="MiseOne"
                title={isMulti ? tFi("ui.printMenuDialog.dailyPrepLists", "Daily Prep Lists") : tFi("ui.printMenuDialog.dailyPrepList", "Daily Prep List")}
            />
            {days.map((day, di) => (
                <section key={day.date}
                         className={`${di > 0 ? "print-page-break" : ""} mb-8 break-inside-avoid`}>
                    <DayHeading day={day} />
                    <PrepBody day={day} />
                    <KitchenNotesBlock />
                </section>
            ))}
        </div>
    );
}

function PrepBody({ day }) {
    if (!day.dishes || day.dishes.length === 0) {
        return <p className="text-sm text-neutral-500 italic">{tFi("ui.printMenuDialog.noPrepListForThisDay", "No prep list for this day.")}</p>;
    }
    return day.dishes.map((dish, di) => (
        <div key={di} className="mb-5 break-inside-avoid">
            <div className="font-semibold text-lg">{dish.dish_name}</div>
            {(dish.groups || []).map((g, gi) => (
                <div key={gi} className="mt-2 ml-4">
                    <div className="text-sm font-semibold uppercase tracking-wide text-neutral-700">
                        {g.name}
                    </div>
                    <ul className="mt-1">
                        {(g.tasks || []).map((t, ti) => (
                            <li key={ti} className="text-sm ml-4 flex items-start gap-2">
                                <span className="inline-block h-3.5 w-3.5 border border-neutral-500 mt-0.5"></span>
                                <span>{typeof t === "string" ? t : t.label}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    ));
}

function KitchenNotesBlock() {
    return (
        <section className="mt-6 break-inside-avoid">
            <div className="text-sm font-semibold uppercase tracking-wide">{tFi("ui.printMenuDialog.kitchenNotes", "Kitchen notes")}</div>
            <div className="mt-2 space-y-4">
                {[1, 2, 3].map((n) => (
                    <div key={n} className="border-b border-neutral-400 h-6"></div>
                ))}
            </div>
        </section>
    );
}

// ─────────────────────────────────────────────── Print CSS
if (typeof document !== "undefined" && !document.getElementById("wm-print-css")) {
    const style = document.createElement("style");
    style.id = "wm-print-css";
    style.textContent = `
        @media print {
          body * { visibility: hidden !important; }
          [data-testid="print-menu-dialog"], [data-testid="print-menu-dialog"] * {
            visibility: visible !important;
          }
          [data-testid="print-menu-dialog"] {
            position: absolute !important;
            left: 0; top: 0; width: 100%;
            max-width: none !important; max-height: none !important;
            box-shadow: none !important; border: 0 !important;
            padding: 0 !important; overflow: visible !important;
          }
          .print\\:hidden { display: none !important; }
          .print\\:p-0 { padding: 0 !important; }
          .print-page-break { page-break-before: always; }
          @page { size: A4; margin: 16mm; }
        }
    `;
    document.head.appendChild(style);
}
